import type { ReactElement, ReactNode } from 'react';
import type { To, Location } from 'react-router';
import { useNavigate, useLocation } from 'react-router';
import type { BaseLinkProps } from 'src/components/navigation/link';
import { ExternalLink, Link } from 'src/components/navigation/link';
import { setLocationHref } from 'src/utils/location-usage';

type WithOptionalReferrerState = { referrer?: ReferrerState } | void;

type ReferrerLocation = Partial<Location<WithOptionalReferrerState>>;

export type ReferrerState = {
  location: ReferrerLocation | string;
  label: string;
};

type ReferrerLinkProps = BaseLinkProps & {
  children: ReactNode;
  'data-testid'?: string;
};

export type Referrer = ReferrerState & {
  /**
   * Creates a link that points a custom referrer link.
   */
  createReferrerLink: (
    build: (referrerState: ReferrerState) => ReferrerLinkProps
  ) => ReactElement;
  /**
   * Navigate to the parent page.
   */
  goTo: () => void;
  /**
   * Merges the referrer state of the parent page into the given location descriptor to be used in a link.
   *
   * Note: While we usually work with a "location" object, links handle
   * the state separate from all other fields. Those other fields are called
   * a "to" object.
   */
  withReferrerLinkProps: (location: Partial<Location<{}>> | string) => {
    to: To;
    state: { referrer: ReferrerState }; // note: we will also merge `location.state` into this
  };
};

/**
 * Resolves the current page referrer and gives you helpers to navigate back to it.
 * Use it on a child page to read the passed referrer, fall back to a default parent page, and drive back links, back buttons, or redirects after submit.
 * See detailed documentation: https://storybook.service.prisma-capacity.cloud/master/?path=/docs/developer-navigation-with-referrers--docs
 */
export function useReferrer(defaultReferrer: ReferrerState): Referrer {
  const { state } = useLocation<WithOptionalReferrerState>();
  const referrerState = state?.referrer ?? defaultReferrer;

  const navigate = useNavigate();

  const goTo = () => {
    if (isExternal(referrerState.location)) {
      setLocationHref(referrerState.location);
    } else {
      navigate(referrerState.location, { state: referrerState.location.state });
    }
  };

  const withReferrerLinkProps = (location: Partial<Location<{}>> | string) => {
    if (typeof location === 'string') {
      return {
        to: location,
        state: { referrer: referrerState },
      };
    } else {
      const { state, ...to } = location;
      return {
        to,
        state: { ...state, referrer: referrerState },
      };
    }
  };

  const createReferrerLink = (
    build: (referrerState: ReferrerState) => ReferrerLinkProps
  ) => {
    const linkProps = build(referrerState);
    const { children, ...rest } = linkProps;

    if (isExternal(referrerState.location)) {
      return (
        <ExternalLink href={referrerState.location} {...rest}>
          {children}
        </ExternalLink>
      );
    }

    return (
      <Link
        to={referrerState.location}
        state={referrerState.location.state}
        {...rest}
      >
        {children}
      </Link>
    );
  };

  return { ...referrerState, createReferrerLink, goTo, withReferrerLinkProps };
}

type UseSaveReferrerOptions = {
  label: string;
  // TODO: Add support for anchor in a follow-up merge request.
  // anchor: string;
};

export type SavedReferrer = ReferrerState & {
  /**
   * Merges the referrer state of the current page into the given location descriptor to be used in a link.
   *
   * Note: While we usually work with a "location" object, links handle
   * the state separate from all other fields. Those other fields are called
   * a "to" object.
   */
  withReferrerLinkProps: (location: Partial<Location<{}>> | string) => {
    to: To;
    state: { referrer: ReferrerState }; // note: we will also merge `location.state` into this
  };
};

/**
 * Saves the current page as a referrer so child pages can remember where the user came from.
 * Use it on a parent page and pass it forward with `withReferrerLinkProps(...)` when linking to details, edit pages or other child pages.
 * See detailed documentation: https://storybook.service.prisma-capacity.cloud/master/?path=/docs/developer-navigation-with-referrers--docs
 */
export function useSaveReferrer({
  label,
}: UseSaveReferrerOptions): SavedReferrer {
  const location = useLocation<WithOptionalReferrerState>();
  const referrerState: ReferrerState = { location, label };

  const withReferrerLinkProps = (location: Partial<Location<{}>> | string) => {
    if (typeof location === 'string') {
      return {
        to: location,
        state: { referrer: referrerState },
      };
    } else {
      const { state, ...to } = location;
      return {
        to,
        state: { ...state, referrer: referrerState },
      };
    }
  };

  return { ...referrerState, withReferrerLinkProps };
}

export function isReferrer(value: unknown): value is Referrer {
  return (
    typeof value === 'object' && value !== null && 'createReferrerLink' in value
  );
}

function isExternal(location: ReferrerLocation | string): location is string {
  return (
    typeof location === 'string' &&
    (location.startsWith('https://') || location.startsWith('http://'))
  );
}
